// Pure GA4 → PostHog event mapping (contract C4). No window, document or
// storage access: callers pass the payloads in and send the result through
// productAnalytics.ts. Every value that leaves here is a number, a finite
// enum or catalog text; customer text is limited to the scrubbed search query.

import type { PhProps } from "./productAnalytics";

export type PhEvent = { event: string; props: PhProps };

export type PhProduct = {
  product_id: string;
  sku?: string;
  name?: string;
  brand?: string;
  price: number;
  quantity: number;
  position?: number;
};

export type PhCheckoutType = "standard" | "express";

export const PH_PRODUCTS_LIMIT = 50;
export const PH_SEARCH_QUERY_MAX = 100;
export const PH_CURRENCY = "USD";

const TEXT_MAX = 200;
const EMAIL_LIKE = /[^\s@]+@[^\s@]+/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A finite amount rounded to cents; anything else is 0. */
export const toMoney = (value: unknown): number => {
  const parsed =
    typeof value === "number"
      ? value
      : Number(String(value ?? "").replace(/[^0-9.-]+/g, ""));
  if (!Number.isFinite(parsed)) return 0;
  // Half away from zero; the epsilon absorbs binary noise (1.005 → 1.01).
  const cents = Math.round(Math.abs(parsed) * 100 + 1e-7);
  return cents === 0 ? 0 : (parsed < 0 ? -cents : cents) / 100;
};

const catalogText = (value: unknown): string | undefined => {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const text = String(value).replace(/\s+/g, " ").trim().slice(0, TEXT_MAX);
  if (!text || EMAIL_LIKE.test(text)) return undefined;
  return text;
};

const enumValue = (value: unknown, fallback?: string): string | undefined => {
  const text = String(value ?? "")
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9_.-]{0,63}$/.test(text) ? text : fallback;
};

const positiveInteger = (value: unknown, fallback = 1): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const currencyOf = (ecommerce: Record<string, unknown>): string => {
  const code = String(ecommerce.currency ?? "").toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : PH_CURRENCY;
};

const withoutUndefined = (props: PhProps): PhProps => {
  const clean: PhProps = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === "") continue;
    // Defence in depth: no property ever carries an email address.
    if (
      key === "email" ||
      (typeof value === "string" && EMAIL_LIKE.test(value))
    )
      continue;
    clean[key] = value;
  }
  return clean;
};

/** One GA4 item → one PostHog product, or null without a product id. */
export const phProductFromGa4Item = (item: unknown): PhProduct | null => {
  if (!isRecord(item)) return null;
  const productId = catalogText(item.item_id);
  if (!productId) return null;
  const product: PhProduct = {
    product_id: productId,
    price: toMoney(item.price),
    quantity: positiveInteger(item.quantity),
  };
  const sku = catalogText(item.sku);
  if (sku) product.sku = sku;
  const name = catalogText(item.item_name);
  if (name) product.name = name;
  const brand = catalogText(item.item_brand);
  if (brand) product.brand = brand;
  if (Number.isInteger(item.index) && (item.index as number) >= 0)
    product.position = item.index as number;
  return product;
};

const validProducts = (items: unknown): PhProduct[] =>
  (Array.isArray(items) ? items : [])
    .map(phProductFromGa4Item)
    .filter((product): product is PhProduct => product !== null);

/** GA4 items → at most PH_PRODUCTS_LIMIT PostHog products. */
export const phProducts = (items: unknown): PhProduct[] =>
  validProducts(items).slice(0, PH_PRODUCTS_LIMIT);

const productsProps = (items: unknown): PhProps => {
  const all = validProducts(items);
  return {
    products: all.slice(0, PH_PRODUCTS_LIMIT),
    product_count: all.length,
  };
};

const firstProduct = (items: unknown): PhProduct | null =>
  validProducts(items)[0] || null;

const promotionsOf = (ecommerce: Record<string, unknown>) =>
  (Array.isArray(ecommerce.items) ? ecommerce.items : [])
    .filter(isRecord)
    .map((item) =>
      withoutUndefined({
        promotion_id: catalogText(item.promotion_id),
        name: catalogText(item.promotion_name),
        creative: catalogText(item.creative_name),
      })
    )
    .filter((promotion) => promotion.promotion_id)
    .slice(0, PH_PRODUCTS_LIMIT);

const promotionProps = (ecommerce: Record<string, unknown>): PhProps | null => {
  const promotions = promotionsOf(ecommerce);
  if (!promotions.length) return null;
  return {
    ...(promotions.length === 1 ? promotions[0] : {}),
    promotions,
    promotion_count: promotions.length,
  };
};

const checkoutTypeOf = (value: unknown): PhCheckoutType =>
  value === "express" ? "express" : "standard";

/** Caller context: numbers and booleans as they are, text as catalog text. */
const callerProps = (extra?: PhProps): PhProps => {
  const props: PhProps = {};
  for (const [key, value] of Object.entries(isRecord(extra) ? extra : {})) {
    if (typeof value === "string") props[key] = catalogText(value);
    else if (typeof value === "boolean") props[key] = value;
    else if (typeof value === "number" && Number.isFinite(value))
      props[key] = value;
  }
  return props;
};

/**
 * GA4 ecommerce event → PostHog event, or null when PostHog takes the event
 * from elsewhere:
 * - add_to_cart / remove_from_cart: the Vuex cart diff in posthog-tracking.vue
 *   sees every add and remove path, so this sink stays silent (no double count);
 * - begin_checkout: Checkout Started comes from the /store/checkout route entry
 *   (standard) and the product-page wallet begin (express), one per visit;
 * - purchase: Order Completed is sent by the success page from the
 *   `vf_pending_purchase` stash (buildPosthogOrderCompleted).
 * `extra` adds caller context (surface, checkout_type, brand).
 */
export const mapGa4EcommerceToPosthog = (
  event: string,
  ecommerce: unknown,
  extra?: PhProps
): PhEvent | null => {
  if (!isRecord(ecommerce)) return null;
  const currency = currencyOf(ecommerce);
  const value =
    ecommerce.value === undefined ? undefined : toMoney(ecommerce.value);
  let name = "";
  let props: PhProps | null = null;

  switch (event) {
    case "view_item_list":
      name = "Product List Viewed";
      props = {
        list_id: catalogText(ecommerce.item_list_id),
        list_name: catalogText(ecommerce.item_list_name),
        ...productsProps(ecommerce.items),
        currency,
      };
      break;
    case "select_item": {
      const product = firstProduct(ecommerce.items);
      if (!product) return null;
      name = "Product Clicked";
      props = {
        ...product,
        list_name: catalogText(ecommerce.item_list_name),
        currency,
      };
      break;
    }
    case "view_item": {
      const product = firstProduct(ecommerce.items);
      if (!product) return null;
      name = "Product Viewed";
      props = { ...product, value, currency };
      break;
    }
    case "add_to_wishlist": {
      const product = firstProduct(ecommerce.items);
      if (!product) return null;
      name = "Product Added to Wishlist";
      props = { ...product, value, currency };
      break;
    }
    case "view_cart":
      name = "Cart Viewed";
      props = { value, currency, ...productsProps(ecommerce.items) };
      break;
    case "add_shipping_info":
      name = "Checkout Step Completed";
      props = {
        step: 2,
        step_name: "shipping",
        shipping_method: catalogText(ecommerce.shipping_tier),
        checkout_type: checkoutTypeOf(
          extra?.checkout_type ?? ecommerce.checkout_type
        ),
        value,
        currency,
      };
      break;
    case "add_payment_info":
      name = "Payment Info Entered";
      props = {
        payment_method: enumValue(ecommerce.payment_type, "other"),
        checkout_type: checkoutTypeOf(
          extra?.checkout_type ?? ecommerce.checkout_type
        ),
        value,
        currency,
      };
      break;
    case "view_promotion":
    case "select_promotion":
      props = promotionProps(ecommerce);
      if (!props) return null;
      name =
        event === "view_promotion" ? "Promotion Viewed" : "Promotion Clicked";
      break;
    default:
      return null;
  }

  return {
    event: name,
    props: withoutUndefined({ ...props, ...callerProps(extra) }),
  };
};

/** Checkout Started from a GA4-shaped cart payload (cartToGa4Ecommerce). */
export const posthogCheckoutStarted = (
  ecommerce: unknown,
  checkoutType: PhCheckoutType
): PhEvent => {
  const source = isRecord(ecommerce) ? ecommerce : {};
  return {
    event: "Checkout Started",
    props: withoutUndefined({
      value: toMoney(source.value),
      currency: currencyOf(source),
      ...productsProps(source.items),
      coupon: catalogText(source.coupon),
      checkout_type: checkoutTypeOf(checkoutType),
    }),
  };
};

/** Checkout Step Completed for a step that has no GA4 event (information). */
export const posthogCheckoutStep = (
  step: number,
  stepName: string,
  checkoutType: PhCheckoutType = "standard"
): PhEvent => ({
  event: "Checkout Step Completed",
  props: withoutUndefined({
    step: positiveInteger(step),
    step_name: enumValue(stepName),
    checkout_type: checkoutTypeOf(checkoutType),
  }),
});

/**
 * Product Added / Product Removed for one product of a confirmed cart change.
 * `item` is a GA4 item (cartLineToGa4Item); `quantity` is the changed units.
 */
export const posthogCartChange = (
  change: "added" | "removed",
  item: unknown,
  quantity: number,
  extra?: PhProps
): PhEvent | null => {
  const product = phProductFromGa4Item(item);
  const units = positiveInteger(quantity, 0);
  if (!product || !units) return null;
  return {
    event: change === "added" ? "Product Added" : "Product Removed",
    props: withoutUndefined({
      ...product,
      quantity: units,
      value: toMoney(product.price * units),
      currency: PH_CURRENCY,
      ...callerProps(extra),
    }),
  };
};

const PHONE_ONLY = /^[+()\d\s./-]+$/;
const PHONE_RUN = /(?:\d[\s()./-]*){10,}/;
// Quotes around a number do not make it less of a phone number.
const QUOTE_CHARS = /["'“”‘’]/g;

/**
 * The one free-text property: trimmed, whitespace collapsed, at most 100
 * characters. Null when empty, when it holds an "@" (an email address or a
 * handle) or when it looks like a phone number (7+ digits with only phone
 * punctuation and quotes, or a run of 10+ digits anywhere).
 */
export const scrubSearchQuery = (raw: unknown): string | null => {
  if (typeof raw !== "string") return null;
  const query = raw.replace(/\s+/g, " ").trim();
  if (!query || query.includes("@")) return null;
  const digits = query.replace(/\D/g, "").length;
  const unquoted = query.replace(QUOTE_CHARS, "").trim();
  if ((PHONE_ONLY.test(unquoted) && digits >= 7) || PHONE_RUN.test(query))
    return null;
  return query.slice(0, PH_SEARCH_QUERY_MAX).trim();
};

export type PhSearchSurface = "results_page" | "header_dropdown";

export const posthogProductsSearched = (
  rawQuery: unknown,
  resultsCount: unknown,
  surface: PhSearchSurface
): PhEvent => {
  const count = Number(resultsCount);
  return {
    event: "Products Searched",
    props: withoutUndefined({
      query: scrubSearchQuery(rawQuery) ?? undefined,
      results_count: Number.isInteger(count) && count >= 0 ? count : 0,
      surface,
    }),
  };
};

// Behaviour events PostHog takes from a dedicated ecommerce event instead:
// a footer signup sends Newsletter Subscribed (new-footer.vue), so its GA4
// behaviour twin is not forwarded (no double count).
const BEHAVIOR_EVENTS_NOT_FORWARDED: ReadonlySet<string> = new Set([
  "newsletter_subscribe_success",
]);

/**
 * behaviorAnalytics.ts events keep their snake_case name and finite detail.
 * Null for events PostHog counts elsewhere (BEHAVIOR_EVENTS_NOT_FORWARDED).
 */
export const posthogBehaviorEvent = (
  event: unknown,
  detail?: unknown
): PhEvent | null => {
  if (typeof event !== "string" || !/^[a-z][a-z0-9_]{0,63}$/.test(event))
    return null;
  if (BEHAVIOR_EVENTS_NOT_FORWARDED.has(event)) return null;
  const behaviorDetail =
    typeof detail === "string" && /^[a-z][a-z0-9_]{0,63}$/.test(detail)
      ? detail
      : undefined;
  return {
    event,
    props: withoutUndefined({ behavior_detail: behaviorDetail }),
  };
};

// One plain address: the same shape the client adapter accepts for a person
// email (posthogRuntime.ts), at most 254 characters.
const GUEST_EMAIL_CHARS = `[^\\s@<>()[\\]\\\\,;:"'/?#&=]`;
const GUEST_EMAIL = new RegExp(
  `^${GUEST_EMAIL_CHARS}+@${GUEST_EMAIL_CHARS}+\\.${GUEST_EMAIL_CHARS}{2,}$`
);
export const PH_GUEST_EMAIL_MAX = 254;

/** The trimmed order email when it is one plain address, else null. */
export const posthogGuestEmail = (raw: unknown): string | null => {
  if (typeof raw !== "string") return null;
  const email = raw.trim();
  return email.length <= PH_GUEST_EMAIL_MAX && GUEST_EMAIL.test(email)
    ? email
    : null;
};

export type PhGuestPerson = { email: string };

/**
 * The `posthogPerson` part of the `vf_pending_purchase` stash (owner decision
 * D5b): a guest buyer's order email, which the success page puts on the
 * anonymous PostHog profile. Null unless analytics is allowed, the shopper is
 * a guest (no customer account signed in) and the email is valid. It never
 * goes into an event property; the stash is removed with the rest of it.
 */
export const buildPosthogGuestPerson = ({
  analyticsAllowed,
  guest,
  email,
}: {
  analyticsAllowed: boolean;
  guest: boolean;
  email: unknown;
}): PhGuestPerson | null => {
  if (analyticsAllowed !== true || guest !== true) return null;
  const valid = posthogGuestEmail(email);
  return valid ? { email: valid } : null;
};

/** Re-checks the stashed person before use: the email or null. */
export const posthogGuestEmailFromStash = (person: unknown): string | null =>
  isRecord(person) ? posthogGuestEmail(person.email) : null;

// The success page's expiry for `vf_pending_purchase` (checkout-success.vue
// PENDING_PURCHASE_TTL_MS): an older stash is never sent.
export const PH_PENDING_PURCHASE_TTL_MS = 2 * 60 * 60 * 1000;

/**
 * An abandoned payment (3-D Secure or a redirect never completed, a tab
 * closed) leaves `vf_pending_purchase` in storage until the next checkout or
 * success page. Once the stash is past the success page's expiry its guest
 * email can never be used, so it goes: returns the stash JSON with
 * `posthogPerson: null`, or null when nothing has to change (fresh stash, no
 * person, unreadable JSON).
 */
export const stashWithoutExpiredGuestPerson = (
  raw: unknown,
  now: number
): string | null => {
  if (typeof raw !== "string" || !raw) return null;
  let stash: unknown;
  try {
    stash = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(stash) || stash.posthogPerson == null) return null;
  const ts = stash.ts;
  const fresh =
    typeof ts === "number" &&
    Number.isFinite(ts) &&
    now - ts <= PH_PENDING_PURCHASE_TTL_MS;
  return fresh ? null : JSON.stringify({ ...stash, posthogPerson: null });
};

const ORDER_COMPLETED_KEYS = [
  "revenue",
  "currency",
  "subtotal",
  "shipping",
  "tax",
  "discount",
  "coupon",
  "products",
  "product_count",
  "checkout_type",
] as const;

/**
 * The `posthog` part of the `vf_pending_purchase` stash (owner decision D6).
 * `revenue` is the grand total charged in decimal USD (cents / 100);
 * `subtotal` is the GA4 merchandise value. Never an order id or number.
 * Without a valid grand total the revenue falls back to
 * subtotal + shipping + tax.
 */
export const buildPosthogOrderCompleted = ({
  ecommerce,
  grandTotalCents,
  checkoutType,
}: {
  ecommerce: unknown;
  grandTotalCents?: unknown;
  checkoutType: PhCheckoutType;
}): PhProps | null => {
  if (!isRecord(ecommerce)) return null;
  const subtotal = toMoney(ecommerce.value);
  const shipping = toMoney(ecommerce.shipping);
  const tax = toMoney(ecommerce.tax);
  // Only a real integer amount counts: Number(null) would read as 0 cents.
  const revenue =
    typeof grandTotalCents === "number" &&
    Number.isInteger(grandTotalCents) &&
    grandTotalCents >= 0
      ? toMoney(grandTotalCents / 100)
      : toMoney(subtotal + shipping + tax);
  const items = Array.isArray(ecommerce.items) ? ecommerce.items : [];
  const couponLines = items.filter(
    (item) => isRecord(item) && item.coupon_discount !== undefined
  );
  const coupon = catalogText(ecommerce.coupon);
  // Standard checkout items carry the coupon share per unit; the express
  // quote does not, so its discount is unknown when a coupon applies.
  const discount = couponLines.length
    ? toMoney(
        couponLines.reduce(
          (sum: number, item: any) =>
            sum +
            (Number(item.coupon_discount) || 0) *
              positiveInteger(item.quantity),
          0
        )
      )
    : coupon
    ? undefined
    : 0;
  return withoutUndefined({
    revenue,
    currency: currencyOf(ecommerce),
    subtotal,
    shipping,
    tax,
    discount,
    coupon,
    ...productsProps(items),
    checkout_type: checkoutTypeOf(checkoutType),
  });
};

/**
 * Re-checks a stashed Order Completed payload before it is sent: only the
 * known keys survive, so an older or altered stash cannot add an order id.
 */
export const posthogOrderCompletedFromStash = (
  stash: unknown
): PhEvent | null => {
  if (!isRecord(stash)) return null;
  const props: PhProps = {};
  for (const key of ORDER_COMPLETED_KEYS) {
    if (stash[key] !== undefined) props[key] = stash[key];
  }
  if (typeof props.revenue !== "number" || !Number.isFinite(props.revenue))
    return null;
  props.products = (Array.isArray(props.products) ? props.products : [])
    .filter(isRecord)
    .map((product) =>
      phProductFromGa4Item({
        item_id: product.product_id,
        sku: product.sku,
        item_name: product.name,
        item_brand: product.brand,
        price: product.price,
        quantity: product.quantity,
        index: product.position,
      })
    )
    .filter(Boolean)
    .slice(0, PH_PRODUCTS_LIMIT);
  for (const key of ["subtotal", "shipping", "tax", "discount", "revenue"]) {
    if (props[key] !== undefined) props[key] = toMoney(props[key]);
  }
  const count = Number(props.product_count);
  props.product_count =
    Number.isInteger(count) && count >= 0
      ? count
      : (props.products as unknown[]).length;
  props.coupon = catalogText(props.coupon);
  props.currency =
    typeof props.currency === "string" && /^[A-Z]{3}$/.test(props.currency)
      ? props.currency
      : PH_CURRENCY;
  props.checkout_type = checkoutTypeOf(props.checkout_type);
  return { event: "Order Completed", props: withoutUndefined(props) };
};
